import { coreGameStateFromDirectory, lookupCoreGameState, useCoreGameStatesStore } from './coreGameStates';
import { gameStateReadiness } from '@/lib/gameState/readiness';
import { coreGameStateKey, type CoreGameState } from '@/lib/gameState/admission';
import type { OddsStreamGameState } from './oddsStream';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { FEED_STALE_MS } from '@/lib/sport/liveDisplayState';

/** Comparison configuration alone never changes primary readers. A build must
 * explicitly opt in, with its separately verified Core URL/token — EXCEPT a
 * game-state lane published in the core directory, which turns them on
 * whatever the env says (directory-edgelike-cores D3: Dan, no site env
 * edits). Fixed for the page's life, like the lane itself. */
export function primaryGameStateEnabled(): boolean {
  return import.meta.env.VITE_HYDRA_GAMESTATE_PRIMARY === 'true' || coreGameStateFromDirectory();
}

export function readPrimaryGameState(key: string | null | undefined): CoreGameState | null {
  if (!primaryGameStateEnabled() || !key) return null;
  const view = useCoreGameStatesStore.getState();
  const hit = lookupCoreGameState(view.records, key);
  return hit ? gameStateReadiness(hit.coreKey, hit.row, view.status, performance.now(), FEED_STALE_MS).canonical : null;
}

/** Only named participants may orient scores. No anonymous tennis A/B, no
 * legacy consensus or situation fields copied onto a different source. */
export function projectPrimaryGameState(state: CoreGameState | null,
  teams?: readonly [string, string]): OddsStreamGameState | null {
  if (!state) return null;
  const same = !teams || teams[0] === state.awayTeam && teams[1] === state.homeTeam;
  const swapped = !!teams && teams[0] === state.homeTeam && teams[1] === state.awayTeam;
  if (!same && !swapped) return null;
  const away = swapped ? state.homeScore : state.awayScore;
  const home = swapped ? state.awayScore : state.homeScore;
  return { statusId: state.statusId, source: state.source, sport: state.sport,
    ...(away !== null ? { awayScore: away } : {}),
    ...(home !== null ? { homeScore: home } : {}),
    ...(state.gameClock !== null ? { gameClock: state.gameClock } : {}),
    ...(state.periodTypeId !== null ? { periodTypeId: state.periodTypeId } : {}),
    ...(state.periodLabel != null ? { periodLabel: state.periodLabel } : {}),
    lastUpdateMs: state.lastUpdateMs, lastStateChangeMs: state.lastStateChangeMs };
}

/** Read-only subscriber to the route's shared transport plus the existing
 * display clock; no per-row interests, sockets, polling or execution.
 * A configured outage stays unknown instead
 * of silently borrowing a legacy record after source retirement. */
export function usePrimaryGameState(key: string | null | undefined,
  teams?: readonly [string, string]): OddsStreamGameState | null {
  const enabled = primaryGameStateEnabled();
  const coreKey = enabled ? coreGameStateKey(key) : null;
  const row = useCoreGameStatesStore(s => coreKey ? s.records.get(coreKey) : undefined);
  const status = useCoreGameStatesStore(s => enabled ? s.status : 'stopped');
  useSharedNow(1000, enabled && row !== undefined);
  const state = coreKey
    ? gameStateReadiness(coreKey, row, status, performance.now(), FEED_STALE_MS).canonical : null;
  return projectPrimaryGameState(state, teams);
}
